import styled from '@emotion/styled';
import { useRequest } from 'ahooks';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Table, Input, Pagination } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { unstable_batchedUpdates } from 'react-dom';
import { getDepartmentStoreConsultationDistribution } from 'services/api/department/department';
import ConsultationPopover from './ConsultationPopover';
import ExportExcel from './ExportExcel';
import { useGetRegionLabel } from 'hooks/business/useRegion';

type queryParams = Parameters<typeof getDepartmentStoreConsultationDistribution>[0];

/**
 * 店铺咨询分配
 */
function StoreConsultationDistribution(props: { departmentId: number }) {
  const { departmentId } = props;
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });
  const [keyword, setKeyword] = useState('');
  const params = {
    departmentId,
    ...pagination,
    keyword: keyword,
  };

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const { data, loading, refresh } = useRequest<
    StoreHomeV2.StoreConsultationDistribution,
    [queryParams]
  >(() => getDepartmentStoreConsultationDistribution(params), {
    refreshDeps: [keyword, pagination.page, pagination.pageSize],
  });

  const { storeInfos, total } = data || ({} as StoreHomeV2.StoreConsultationDistribution);

  const onSearch = (value: string) => {
    if (keyword === value) {
      refresh();
    } else {
      unstable_batchedUpdates(() => {
        setKeyword(value);
        setPagination({
          page: 1,
          pageSize: 10,
        });
      });
    }
  };

  return (
    <Container>
      <SearchBar>
        <div>{t('feedback.共x项', { x: total })}</div>
        <div className="right">
          <Input.Search
            placeholder={t('department.输入店铺名称/别名')}
            allowClear
            prefix={<SearchOutlined />}
            onSearch={onSearch}
            enterButton={t('department.搜索')}
            style={{ width: 312 }}
          />
          <ExportExcel departmentId={departmentId} keyword={keyword} />
        </div>
      </SearchBar>
      <Table
        loading={loading}
        rowKey="storeId"
        pagination={false}
        dataSource={storeInfos}
        columns={[
          {
            key: 'externalType',
            dataIndex: 'externalType',
            title: t('market.平台'),
            render: (text: string) => <>{text}</>,
          },
          {
            key: 'region',
            dataIndex: 'region',
            title: t('phrase.站点'),
            render: (text: string) => <>{getRegionLabel(text)}</>,
          },
          {
            key: 'storeName',
            title: t('marketing.店铺名称'),
            render: (_text: string, record) => <>{record.alias || record.storeName}</>,
          },
          {
            key: 'assignConversationInfos',
            dataIndex: 'assignConversationInfos',
            title: t('department.咨询分配情况'),
            render: (
              assignConversationInfos: StoreHomeV2.StoreConsultationDistribution['storeInfos'][number]['assignConversationInfos'],
            ) => {
              return assignConversationInfos.length ? (
                <ConsultationPopover dataSource={assignConversationInfos} />
              ) : (
                <>{t('department.仅超级管理员可见')}</>
              );
            },
          },
        ]}
      />
      <Pagination
        style={{
          float: 'right',
          marginTop: 16,
        }}
        total={total}
        pageSize={pagination.pageSize}
        current={pagination.page}
        onChange={(page, pageSize) => {
          setPagination({
            page,
            pageSize: pageSize || 1,
          });
        }}
        showSizeChanger
        showQuickJumper
        showTotal={(total) => t('feedback.共x项', { x: total })}
      />
    </Container>
  );
}

const Container = styled.div``;
const SearchBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  & > .right {
    display: inline-flex;
    gap: 16px;
  }
`;

export default StoreConsultationDistribution;
